import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { requestJson } from './client';

export const PERIODS = [
  { days: 30, label: '30 дней' },
  { days: 90, label: 'Квартал' },
  { days: 365, label: 'Год' },
] as const;
export type PeriodDays = (typeof PERIODS)[number]['days'];

export interface TaskStats {
  total: number;
  open: number;
  overdue: number;
  completed: number;
  completedLate: number;
}

export interface TaskSyncInfo {
  ready: boolean;
  loaded: number;
  updatedAt: string | null;
}

export interface TaskItem {
  id: number;
  title: string;
  status: number;
  statusLabel: string;
  deadline: string | null;
  createdAt: string | null;
  closedAt: string | null;
  overdue: boolean;
  late: boolean;
  creator: { id: string | null; fullName: string | null } | null;
  url: string;
}

/** Роль человека в задаче. */
export type TaskRole = 'responsible' | 'accomplice' | 'creator' | 'auditor';

export interface TaskRoleCount {
  role: TaskRole;
  label: string;
  open: number;
  overdue: number;
}

export interface EmployeeTasks {
  period: PeriodDays;
  list: 'open' | 'closed';
  role: TaskRole;
  linked: boolean;
  sync: TaskSyncInfo;
  /** Сколько открытых и просроченных задач в каждой роли. */
  roles: TaskRoleCount[];
  stats: TaskStats;
  items: TaskItem[];
  total: number;
}

export interface MetricsScope {
  id: string;
  name: string;
  parentId: string | null;
  depth: number;
  people: number;
}

export interface Metrics {
  period: PeriodDays;
  sync: TaskSyncInfo;
  scopes: MetricsScope[];
  department: { id: string; name: string; path: { id: string; name: string }[] } | null;
  totals: (TaskStats & { people: number }) | null;
  units: (TaskStats & { id: string; name: string; people: number })[];
  people: (TaskStats & { id: string; fullName: string; position: string | null; photoUrl: string | null })[];
}

export interface TasksSyncState {
  configured: boolean;
  state: {
    initialPhase: 'open' | 'closed' | 'done';
    initialLoaded: number;
    initialCompletedAt: string | null;
    changesSince: string;
    lastRunAt: string | null;
    lastError: string | null;
    reconcilePhase: string | null;
    lastReconciledAt: string | null;
  } | null;
}

export function useEmployeeTasks(
  employeeId: string,
  input: { period: PeriodDays; list: 'open' | 'closed'; role: TaskRole; limit: number },
) {
  return useQuery({
    queryKey: ['tasks', employeeId, input.period, input.list, input.role, input.limit],
    queryFn: () =>
      requestJson<EmployeeTasks>(
        `/api/v1/employees/${employeeId}/tasks?period=${input.period}&list=${input.list}&role=${input.role}&limit=${input.limit}`,
      ),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

export function useMetrics(period: PeriodDays, departmentId: string | null) {
  return useQuery({
    queryKey: ['metrics', period, departmentId],
    queryFn: () =>
      requestJson<Metrics>(
        `/api/v1/metrics?period=${period}${departmentId ? `&departmentId=${departmentId}` : ''}`,
      ),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

export function useTasksSyncState() {
  return useQuery({
    queryKey: ['admin', 'tasks-sync'],
    queryFn: () => requestJson<TasksSyncState>('/api/v1/admin/tasks-sync'),
    refetchInterval: 30_000,
  });
}

export const percent = (part: number, whole: number) => (whole ? Math.round((part / whole) * 100) : 0);
